import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Show } from "shared/components";
import { DocumentVerificationType } from "shared/types";
import { CombinedIncome } from "../../types/income";
import { Applicant } from "types/application";
import { VerificationSettings } from "types/landlord/reports";
import { getErrorMessage } from "utils/error";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicantRole } from "utils/getApplicantRole";
import { getIncomeJobName } from "../../utils";
import { IntroView } from "./slices/IntroView";
import { SelectMethodsView } from "./slices/SelectMethodsView";
import { VerificationType } from "./types";
import { RequestSentView } from "./slices/RequestSentView";

enum Views {
  SelectVerificationMethods,
  Confirm,
  RequestSent
}

const JOURNEY = [
  Views.SelectVerificationMethods,
  Views.Confirm,
  Views.RequestSent
];

export const SendRequestFlow = ({
  opened,
  onClose,
  onBack,
  onSuccess,
  applicant,
  applicationId,
  verificationId,
  income,
  settings
}: {
  opened: boolean;
  onClose: () => void;
  onBack: () => void;
  applicationId: number | string;
  applicant: Applicant;
  verificationId: number | string;
  income: CombinedIncome;
  onSuccess: () => void;
  settings?: VerificationSettings;
}) => {
  const [view, setView] = useState<Views>(Views.SelectVerificationMethods);
  const [selectedMethods, setSelectedMethods] = useState<VerificationType[]>(
    []
  );
  const [selectedDocTypes, setSelectedDocTypes] = useState<
    DocumentVerificationType[]
  >([]);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationKey: [
      "SEND_REVERIFY_REQUEST",
      "APPLICATION",
      String(applicationId),
      "VERIFICATION",
      String(verificationId),
      "APPLICANT",
      String(applicant.id)
    ],
    mutationFn: () =>
      instance.post(
        `portal/v1/applications/${applicationId}/verifications/${verificationId}/reverify_requests?applicant_id=${applicant.id}`,
        {
          requests: [
            {
              income_id: income.id,
              methods: [
                ...selectedMethods.filter(
                  (m) => m !== VerificationType.Documents
                ),
                ...selectedDocTypes
              ]
            }
          ]
        }
      ),
    onError: (err) => {
      toast.error(
        getErrorMessage(err, "Error sending re-verification request")
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "APPLICATION",
          String(applicationId),
          "applicant",
          String(applicant.id)
        ]
      });
      onSuccess();
      setView(Views.RequestSent);
    }
  });

  const { data: reVerificationPrice, isLoading } = useQuery<number>({
    queryKey: ["REVERIFICATION_PRICE"],
    queryFn: () =>
      instance
        .get("portal/v1/partners/combined_income_reverification_price")
        .then(({ data }) => data.combined_income_reverification_price_cents),
    enabled: view === Views.Confirm
  });

  if (!opened || !income) return null;

  const applicantFullName = getFullName({
    firstName: applicant.first_name,
    middleName: applicant.middle_name,
    lastName: applicant.last_name,
    short: true
  });

  const hasValidSelection =
    selectedMethods.length > 0 &&
    (!selectedMethods.includes(VerificationType.Documents) ||
      selectedDocTypes.length > 0);

  return (
    <>
      <div className="flex-grow min-h-0">
        <Show if={view === Views.SelectVerificationMethods}>
          <SelectMethodsView
            onClose={onClose}
            selectedMethods={selectedMethods}
            setSelectedMethods={setSelectedMethods}
            selectedDocTypes={selectedDocTypes}
            setSelectedDocTypes={setSelectedDocTypes}
            settings={settings?.[income.type]}
          />
        </Show>

        <Show if={view === Views.Confirm}>
          <IntroView
            onClose={onClose}
            applicantRole={getApplicantRole(applicant.role)}
            applicantFullName={applicantFullName}
            incomeStartDate={income.start_date || "-"}
            incomeEndDate="Present"
            incomeSource={income.type}
            incomeName={getIncomeJobName(income.additional_data)}
            cents={reVerificationPrice || 0}
            isLoading={isLoading}
            title="Send request to the applicant"
            description="Applicant will verify via one of the available options"
          />
        </Show>

        <Show if={view === Views.RequestSent}>
          <RequestSentView onClose={onClose} />
        </Show>
      </div>
      <Show if={view !== Views.RequestSent}>
        <div className="px-6 py-4 border-t border-alpha-100 gap-2 flex justify-end self-end w-full">
          <Button
            className="w-[125px]"
            onClick={() => {
              if (view === Views.SelectVerificationMethods) onBack();
              else setView(JOURNEY[JOURNEY.indexOf(view) - 1]);
            }}
          >
            Back
          </Button>

          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-[125px]"
            disabled={
              view === Views.SelectVerificationMethods && !hasValidSelection
            }
            loading={isPending}
            onClick={() => {
              if (view === Views.Confirm) mutate();
              else setView(JOURNEY[JOURNEY.indexOf(view) + 1]);
            }}
          >
            {view === Views.Confirm ? "Send request" : "Continue"}
          </Button>
        </div>
      </Show>
    </>
  );
};
